import { useCallback, useMemo } from "react";
import { FinancialDocumentBulkActionTypes } from "@common/constants";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { DocumentCategory } from "@common/types/documentCategory";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useEntitiesById } from "@hooks/useEntities";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { isUncategorizedDocumentTaxonomy } from "@common/helpers/documentCategoryFilter";
import { useDocumentCategories } from "@api/documentCategory.api";
import { InjectedCommandOption } from "@components/commands/lib";
import { useEntityCommandOptions } from "@components/commands/useEntityCommandOptions";
import commandStyles from "@components/commands/Commands.module.scss";
import { AccountInstitutionIcon } from "@components/institution/AccountInstitutionIcon";
import { useOverlays } from "contexts/OverlayContext";
import { DocumentCategoryIcon } from "./DocumentCategoryIcon";
import { useFinancialDocumentBulkActions } from "./FinancialDocumentBulkActions/FinancialDocumentBulkActions";

const OTHER_ACCOUNTS_HEADER = "Other accounts";

interface UseDocumentCommandOptionsParams {
    isFieldLocked: boolean;
}

export function useDocumentCommandOptions({
    isFieldLocked,
}: UseDocumentCommandOptionsParams) {
    const bulkActions = useFinancialDocumentBulkActions();
    const accounts = useFinancialAccounts();
    const entitiesById = useEntitiesById();
    const { openOverlay } = useOverlays();
    const canCreateCategory = useCanPerformWorkspaceAction(
        WorkspaceAction.CUSTOM_CATEGORIES_EDIT,
    );
    const { data: documentCategoriesData } = useDocumentCategories();
    const documentCategories = useMemo(
        () =>
            (documentCategoriesData?.categories ?? []).filter(
                (category) =>
                    !isUncategorizedDocumentTaxonomy(category.documentTaxonomy),
            ),
        [documentCategoriesData?.categories],
    );

    const handleEntitySelect = useCallback(
        (entity: Entity) => {
            bulkActions.run({
                entityId: entity.id,
                type: FinancialDocumentBulkActionTypes.ASSIGN_ENTITY,
            });
        },
        [bulkActions],
    );

    const entityOptions = useEntityCommandOptions({
        onEntitySelect: handleEntitySelect,
        disabled: bulkActions.busy || isFieldLocked,
    });

    const accountOptions = useMemo((): InjectedCommandOption[] => {
        const noneOption: InjectedCommandOption = {
            id: "doc-set-account-none",
            label: "None",
            keywords: ["none", "clear", "remove account"],
            disabled: bulkActions.busy || isFieldLocked,
            onSelect: () => {
                bulkActions.run({
                    financialAccountId: null,
                    type: FinancialDocumentBulkActionTypes.ASSIGN_FINANCIAL_ACCOUNT,
                });
            },
        };

        const resolveEntityName = (
            entityId: number | null,
        ): string | undefined => {
            if (entityId == null) {
                return undefined;
            }
            const name = entitiesById.get(entityId)?.name;
            return name && name.length > 0 ? name : undefined;
        };

        const distinctEntityIds = new Set(
            accounts
                .map((account) => account.entityId)
                .filter((entityId) => entityId != null),
        );
        const hasMultipleEntities = distinctEntityIds.size > 1;

        const accountsByHeader = new Map<string, FinancialAccount[]>();
        for (const account of accounts) {
            const header =
                resolveEntityName(account.entityId) ?? OTHER_ACCOUNTS_HEADER;
            const group = accountsByHeader.get(header) ?? [];
            group.push(account);
            accountsByHeader.set(header, group);
        }

        const groupedAccounts = [...accountsByHeader.entries()].flatMap(
            ([header, headerAccounts]) =>
                headerAccounts.map((account, indexInGroup) => ({
                    account,
                    header,
                    isFirstInGroup: indexInGroup === 0,
                })),
        );

        const options = groupedAccounts.map(
            ({ account, header, isFirstInGroup }) => ({
                id: `doc-set-account-${account.id}`,
                label: account.name ?? "",
                sublabel: account.accountNumberMask
                    ? getAnonymousAccountNumber(account.accountNumberMask)
                    : undefined,
                icon: <AccountInstitutionIcon account={account} size="sm" />,
                iconClassName: commandStyles.accountOptionIcon,
                groupHeader:
                    hasMultipleEntities && isFirstInGroup ? header : undefined,
                keywords: [
                    account.name ?? "",
                    account.accountNumberMask ?? "",
                    header,
                ].filter(Boolean),
                disabled: bulkActions.busy || isFieldLocked,
                onSelect: () => {
                    bulkActions.run({
                        financialAccountId: account.id,
                        type: FinancialDocumentBulkActionTypes.ASSIGN_FINANCIAL_ACCOUNT,
                    });
                },
            }),
        );

        return [noneOption, ...options];
    }, [accounts, entitiesById, bulkActions, isFieldLocked]);

    const buildCategorySelect = useCallback(
        (documentCategoryId: string | null): (() => void) =>
            () =>
                bulkActions.run({
                    documentCategoryId,
                    type: FinancialDocumentBulkActionTypes.EDIT_DOCUMENT_CATEGORY,
                }),
        [bulkActions],
    );

    const handleCategoryCreated = useCallback(
        (category: DocumentCategory) => buildCategorySelect(category.id)(),
        [buildCategorySelect],
    );

    const categoryOptions = useMemo((): InjectedCommandOption[] => {
        const isDisabled = bulkActions.busy;

        const noneOption: InjectedCommandOption = {
            id: "doc-set-category-none",
            label: "None",
            keywords: ["none", "clear", "remove category"],
            disabled: isDisabled,
            onSelect: buildCategorySelect(null),
        };

        const options = documentCategories.flatMap((category) => {
            const parentOption: InjectedCommandOption = {
                id: `doc-set-category-${category.id}`,
                label: category.label,
                icon: (
                    <DocumentCategoryIcon
                        documentTaxonomy={category.documentTaxonomy}
                    />
                ),
                keywords: [category.label],
                disabled: isDisabled,
                onSelect: buildCategorySelect(category.id),
            };

            const subcategoryOptions = (category.subcategories ?? []).map(
                (subcategory) => ({
                    id: `doc-set-category-${subcategory.id}`,
                    label: subcategory.label,
                    className: commandStyles.subcategoryOption,
                    keywords: [subcategory.label, category.label],
                    disabled: isDisabled,
                    onSelect: buildCategorySelect(subcategory.id),
                }),
            );

            return [parentOption, ...subcategoryOptions];
        });

        if (canCreateCategory) {
            options.unshift({
                id: "doc-set-category-create-new",
                label: "Create new document category",
                keywords: ["create", "new", "add", "category"],
                disabled: isDisabled,
                onSelect: () => {
                    openOverlay<"createDocumentCategory">(
                        "createDocumentCategory",
                        { onCategoryCreated: handleCategoryCreated },
                    );
                },
            });
        }

        return [noneOption, ...options];
    }, [
        documentCategories,
        bulkActions.busy,
        buildCategorySelect,
        canCreateCategory,
        openOverlay,
        handleCategoryCreated,
    ]);

    return { entityOptions, accountOptions, categoryOptions };
}
